import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, getStoredUtm } from "@/context/CartContext";
import { X, Plus, Minus, Trash2, Sparkles, Truck } from "lucide-react";
import { trackInitiateCheckout, makeVariantSku } from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const imgUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  return `${BACKEND_URL}${src}`;
};

const CartDrawer = () => {
  const { items, isOpen, close, updateQty, removeItem, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Fetch quote whenever items change
  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    const fetchQuote = async () => {
      setQuoteLoading(true);
      try {
        const res = await axios.post(`${API}/cart/quote`, {
          items: items.map((i) => ({
            product_id: i.product_id,
            size: i.size,
            quantity: i.quantity,
            color: i.color || null,
          })),
          origin_url: window.location.origin,
        });
        if (!cancelled) setQuote(res.data);
      } catch (e) {
        console.warn("[cart] quote failed:", e);
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    };
    fetchQuote();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    // Fire Meta Pixel InitiateCheckout (use the latest quote total if we have it,
    // otherwise compute from cart prices as a fallback).
    const numItems = items.reduce((n, i) => n + i.quantity, 0);
    const fallbackValue = items.reduce((v, i) => v + i.price_usd * i.quantity, 0);
    trackInitiateCheckout({
      content_ids: items.map((i) => makeVariantSku(i.product_id, i.color, i.size)),
      num_items: numItems,
      value: quote?.total_usd ?? fallbackValue,
      currency: "AUD",
    });
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
          color: i.color || null,
        })),
        origin_url: window.location.origin,
        email: email || undefined,
        utm: getStoredUtm(),
      };
      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res?.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Couldn't start checkout. Try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const subtotal = quote?.subtotal_usd ?? 0;
  const discount = quote?.discount_usd ?? 0;
  const shipping = quote?.shipping_usd ?? 0;
  const total = quote?.total_usd ?? 0;
  const promoActive = (quote?.discount_usd ?? 0) > 0;
  const freeShipping = quote && quote.shipping_usd === 0 && quote.item_count > 0;
  const remaining = quote?.free_shipping_remaining_usd ?? 0;
  const meta = quote?.promo_meta;

  // "Add 1 more for 10% off" nudge
  const itemsRemainingForPromo = meta && quote
    ? Math.max(0, meta.multi_buy_threshold - quote.item_count)
    : 0;

  return (
    <>
      <div
        className={`fixed inset-0 bg-[#1C1914]/60 z-50 transition-opacity duration-200 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={close}
        aria-hidden="true"
        data-testid="cart-backdrop"
      />

      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-[#FFFDF5] z-50 border-l-4 border-[#1C1914] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Shopping cart"
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-4 border-[#1C1914]">
          <div>
            <div className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">
              // Your sad cart
            </div>
            <h2 className="font-display text-3xl uppercase leading-none mt-1" data-testid="cart-title">
              Bag ({count})
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="border-2 border-[#1C1914] p-2 hover:bg-[#FDE047]"
            aria-label="Close cart"
            data-testid="cart-close-button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="cart-items">
          {items.length === 0 && (
            <div className="text-center py-12">
              <p className="font-display text-3xl uppercase">Empty.</p>
              <p className="font-body text-sm text-[#1C1914]/70 mt-3">
                Even more disappointing than usual.
              </p>
            </div>
          )}

          {/* Promo banner */}
          {items.length > 0 && quote && (
            <div className="space-y-2" data-testid="cart-promo-banner">
              {promoActive ? (
                <div className="border-2 border-[#1C1914] bg-[#FDE047] px-3 py-2 flex items-center gap-2" data-testid="cart-promo-active">
                  <Sparkles size={16} />
                  <p className="font-condensed text-sm uppercase tracking-widest">
                    {quote.promo_label} applied — saved A${discount.toFixed(2)}
                  </p>
                </div>
              ) : (
                itemsRemainingForPromo > 0 && (
                  <div className="border-2 border-dashed border-[#1C1914] px-3 py-2" data-testid="cart-promo-nudge">
                    <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/80">
                      Add {itemsRemainingForPromo} more tee{itemsRemainingForPromo === 1 ? "" : "s"} for {meta.multi_buy_discount_pct}% off.
                    </p>
                  </div>
                )
              )}
              {freeShipping ? (
                <div className="border-2 border-[#1C1914] bg-[#4A5D23] text-[#FFFDF5] px-3 py-2 flex items-center gap-2" data-testid="cart-shipping-free">
                  <Truck size={16} />
                  <p className="font-condensed text-sm uppercase tracking-widest">
                    Free shipping unlocked.
                  </p>
                </div>
              ) : (
                remaining > 0 && (
                  <div className="border-2 border-dashed border-[#1C1914] px-3 py-2" data-testid="cart-shipping-nudge">
                    <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/80">
                      A${remaining.toFixed(2)} away from free shipping.
                    </p>
                  </div>
                )
              )}
            </div>
          )}

          {items.map((i) => (
            <div
              key={`${i.product_id}-${i.size}-${i.color || ""}`}
              className="flex gap-3 border-2 border-[#1C1914] bg-[#FFFDF5] p-3"
              data-testid={`cart-line-${i.product_id}-${i.size}${i.color ? `-${i.color.toLowerCase()}` : ""}`}
            >
              <img
                src={imgUrl(i.image)}
                alt={i.name}
                className="w-20 h-20 object-cover border-2 border-[#1C1914] flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-lg leading-tight uppercase">{i.name}</div>
                    <div className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">
                      {i.color ? `${i.color} · ` : ""}Size {i.size}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(i.product_id, i.size, i.color)}
                    className="text-[#1C1914]/70 hover:text-[#D95D39]"
                    aria-label="Remove"
                    data-testid={`cart-remove-${i.product_id}-${i.size}${i.color ? `-${i.color.toLowerCase()}` : ""}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="inline-flex border-2 border-[#1C1914]">
                    <button
                      type="button"
                      onClick={() => updateQty(i.product_id, i.size, i.quantity - 1, i.color)}
                      className="px-2 py-1 hover:bg-[#FDE047]"
                      aria-label="Decrease quantity"
                      data-testid={`cart-dec-${i.product_id}-${i.size}${i.color ? `-${i.color.toLowerCase()}` : ""}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-3 py-1 font-display text-base min-w-[36px] text-center">
                      {i.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateQty(i.product_id, i.size, i.quantity + 1, i.color)}
                      className="px-2 py-1 hover:bg-[#FDE047]"
                      aria-label="Increase quantity"
                      data-testid={`cart-inc-${i.product_id}-${i.size}${i.color ? `-${i.color.toLowerCase()}` : ""}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="font-display text-lg">
                    A${(i.price_usd * i.quantity).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t-4 border-[#1C1914] p-5 space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (for the receipt)"
              className="input-duds bg-[#FFFDF5] text-sm"
              data-testid="cart-email-input"
            />

            {quote && (
              <div className="space-y-1 text-sm font-body" data-testid="cart-breakdown">
                <div className="flex items-center justify-between">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">A${subtotal.toFixed(2)}</span>
                </div>
                {promoActive && (
                  <div className="flex items-center justify-between text-[#D95D39]" data-testid="cart-discount-row">
                    <span>{quote.promo_label}</span>
                    <span>-A${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span>{freeShipping ? "Free shipping" : "Shipping (flat)"}</span>
                  <span data-testid="cart-shipping">A${shipping.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between font-display text-2xl pt-2 border-t-2 border-[#1C1914]">
              <span>TOTAL</span>
              <span data-testid="cart-total">A${total.toFixed(2)}</span>
            </div>

            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">
              Taxes calculated at checkout. Apple Pay & Google Pay supported.
            </p>
            <button
              type="button"
              onClick={checkout}
              disabled={loading || quoteLoading}
              className="btn-duds w-full"
              data-testid="cart-checkout-button"
            >
              {loading ? "Loading…" : "Pay now"}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
